import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ShoppingCart, AlertTriangle, Package, Plus, Minus, Trash2,
    Loader2, CheckCircle2, ArrowRight, ChevronLeft, FileText, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';

import PortalLayout from './PortalLayout';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import ProductImage from '../../components/ProductImage';
import { usePortalCart } from '../../context/PortalCartContext';
import { usePortalAuth } from '../../context/PortalAuthContext';

const formatEur = (n) =>
    `€${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ---- Confirmation card shown after a successful submission ----------------
const SubmittedConfirmation = ({ result, onContinue }) => {
    const { t } = useTranslation('portal');
    const isQuotation = result.order_type === 'quotation';
    const showSubtotal = !isQuotation && result.subtotal != null;
    return (
    <div className="bg-white border border-[#EFE5E1] rounded-lg p-10 text-center max-w-xl mx-auto" data-testid="order-submitted-confirm">
        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#1A1A1A] mb-1">
            {isQuotation ? t('cart.quotation_submitted_title') : t('cart.submitted_title')}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
            {isQuotation ? t('cart.quotation_submitted_body') : t('cart.submitted_body')}
        </p>
        <div className="bg-[#FBF8F6] border border-[#EFE5E1] rounded-md p-4 mb-6 text-left space-y-1">
            <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-gray-500">
                    {isQuotation ? t('cart.quotation_number') : t('cart.order_number')}
                </span>
                <span className="text-sm font-mono font-semibold text-[#1A1A1A]" data-testid="order-number">{result.order_number}</span>
            </div>
            <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-gray-500">{t('cart.items')}</span>
                <span className="text-sm font-medium text-[#1A1A1A]">{result.item_count}</span>
            </div>
            {showSubtotal && (
                <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-gray-500">{t('cart.subtotal_ht')}</span>
                    <span className="text-sm font-semibold text-[#1A1A1A]">{formatEur(result.subtotal)}</span>
                </div>
            )}
        </div>
        <Button
            onClick={onContinue}
            data-testid="order-continue-shopping-btn"
            className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full px-6"
        >
            {t('cart.continue_shopping')}
        </Button>
    </div>
    );
};

// ---- Empty cart state ------------------------------------------------------
const EmptyCart = ({ onShop, moq }) => {
    const { t } = useTranslation('portal');
    return (
    <div className="bg-white border border-[#EFE5E1] rounded-lg p-12 text-center" data-testid="cart-empty">
        <ShoppingCart size={32} className="mx-auto text-gray-300 mb-3" />
        <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('cart.empty_title')}</h3>
        <p className="text-sm text-gray-500 mb-5">{t('cart.empty_body', { moq })}</p>
        <Button onClick={onShop} className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full px-5" data-testid="cart-browse-btn">
            {t('cart.empty_cta')}
        </Button>
    </div>
    );
};

// ---- Line item -------------------------------------------------------------
const LineItem = ({ item, moq, step, busy, onChange, onRemove }) => {
    const { t } = useTranslation('portal');
    return (
    <div
        data-testid={`cart-line-${item.sku}`}
        className="flex items-start gap-4 py-5 border-b border-[#F5EEEA] last:border-b-0"
    >
        <div className="w-20 h-20 bg-[#FBF8F6] rounded flex-shrink-0 overflow-hidden">
            <ProductImage product={item} testid={`cart-line-${item.sku}-img`} />
        </div>

        <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
                <div>
                    {item.brand && (
                        <p className="text-[10px] uppercase tracking-[2px] text-[#C08478] font-semibold">{item.brand}</p>
                    )}
                    <h4 className="font-medium text-[#1A1A1A] line-clamp-2" title={item.name}>{item.name}</h4>
                    <p className="text-[11px] text-gray-400 font-mono mt-0.5">{item.sku}</p>
                </div>
                <button
                    onClick={onRemove}
                    disabled={busy}
                    aria-label={t('cart.remove_aria')}
                    data-testid={`cart-remove-${item.sku}`}
                    className="text-gray-400 hover:text-red-500 disabled:opacity-40"
                >
                    <Trash2 size={16} />
                </button>
            </div>

            {item.below_moq && (
                <div
                    data-testid={`cart-moq-warning-${item.sku}`}
                    className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded mb-2"
                >
                    <AlertTriangle size={12} /> {t('cart.moq_warning_line', { moq })}
                </div>
            )}
            {item.out_of_stock && (
                <div className="flex items-center gap-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded mb-2">
                    <AlertTriangle size={12} /> {t('cart.oos_warning_line')}
                </div>
            )}

            <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onChange(Math.max(moq, item.qty - step))}
                        disabled={busy || item.qty <= moq}
                        aria-label={t('add_to_cart.decrease_aria')}
                        data-testid={`cart-line-dec-${item.sku}`}
                        className="w-8 h-8 rounded-full bg-white border border-[#EFE5E1] flex items-center justify-center text-gray-600 hover:bg-[#F5EEEA] disabled:opacity-40"
                    >
                        <Minus size={13} />
                    </button>
                    <span className="text-sm font-semibold w-10 text-center tabular-nums" data-testid={`cart-line-qty-${item.sku}`}>
                        {item.qty}
                    </span>
                    <button
                        onClick={() => onChange(item.qty + step)}
                        disabled={busy}
                        aria-label={t('add_to_cart.increase_aria')}
                        data-testid={`cart-line-inc-${item.sku}`}
                        className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-white hover:bg-[#333] disabled:opacity-40"
                    >
                        <Plus size={13} />
                    </button>
                </div>
                <div className="text-right">
                    {item.price_hidden ? (
                        <p className="text-[11px] italic text-[#C08478]" data-testid={`cart-line-price-hidden-${item.sku}`}>{t('catalog.price_on_request')}</p>
                    ) : (
                        <>
                            <p className="text-[11px] text-gray-400">{formatEur(item.unit_price)} × {item.qty}</p>
                            <p className="text-sm font-bold text-[#1A1A1A]">{formatEur(item.line_total)}</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    </div>
    );
};

// ---- Page ------------------------------------------------------------------
const PortalCart = () => {
    const { t } = useTranslation('portal');
    const navigate = useNavigate();
    const { cart, loading, moq, step, updateItem, removeItem, clear, submit } = usePortalCart();
    const { profile } = usePortalAuth();
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [busyKind, setBusyKind] = useState(null); // 'order' | 'quotation'
    const [submitted, setSubmitted] = useState(null);

    if (loading && !cart) {
        return (
            <PortalLayout>
                <div className="flex items-center justify-center py-32 text-gray-400" data-testid="cart-loading">
                    <Loader2 size={20} className="animate-spin mr-2" /> {t('cart.loading')}
                </div>
            </PortalLayout>
        );
    }

    if (submitted) {
        return (
            <PortalLayout>
                <SubmittedConfirmation
                    result={submitted}
                    onContinue={() => navigate('/portal/catalog')}
                />
            </PortalLayout>
        );
    }

    const items = cart?.items || [];
    const profileComplete = profile?.profile_complete;
    const approved = profile?.is_approved !== false;
    // Price visibility: backend marks each line `price_hidden`. We
    // fall back to the profile flag for empty carts where no item is
    // hydrated yet (controls which CTA the checkout panel shows).
    const pricesHidden = profile?.can_see_prices === false
        || (items.length > 0 && items.every((it) => it.price_hidden));

    // Approved customers must always be able to submit — even with prices
    // hidden (in which case they can only Request a Quotation). MOQ /
    // stock violations and profile completeness still gate the submit.
    const baseSubmittable = items.length > 0
        && !cart?.has_moq_violations
        && profileComplete
        && approved;

    const onChangeQty = async (item, newQty) => {
        if (newQty < moq) return;
        setBusy(true);
        try { await updateItem(item.product_id, newQty); }
        finally { setBusy(false); }
    };

    const onRemove = async (item) => {
        setBusy(true);
        try { await removeItem(item.product_id); }
        finally { setBusy(false); }
    };

    const doSubmit = async (orderType) => {
        if (!baseSubmittable) {
            if (!profileComplete) toast.error(t('cart.errors.complete_profile'));
            return;
        }
        setBusy(true);
        setBusyKind(orderType);
        try {
            const result = await submit(notes, orderType);
            setSubmitted({ ...result, order_type: orderType });
        } catch (err) {
            // toast already shown by context — still log so production
            // error tracking can surface repeat submission failures.
            console.error('[PortalCart] submit failed:', err?.response?.status || err?.message);
        } finally {
            setBusy(false);
            setBusyKind(null);
        }
    };

    return (
        <PortalLayout>
            <div className="space-y-6" data-testid="portal-cart-page">
                <button
                    onClick={() => navigate('/portal/catalog')}
                    className="text-sm text-gray-500 hover:text-[#1A1A1A] flex items-center gap-1"
                    data-testid="cart-back-btn"
                >
                    <ChevronLeft size={16} /> {t('cart.back_to_catalog')}
                </button>

                <div>
                    <p className="text-xs uppercase tracking-[3px] text-[#C08478] font-semibold mb-2">{t('cart.section_label')}</p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('cart.title')}</h1>
                    <p className="text-sm text-gray-500 mt-1">{t('cart.subtitle', { moq })}</p>
                </div>

                {items.length === 0 ? (
                    <EmptyCart onShop={() => navigate('/portal/catalog')} moq={moq} />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
                        {/* Line items */}
                        <div className="bg-white border border-[#EFE5E1] rounded-lg px-6">
                            <div data-testid="cart-lines">
                                {items.map((it) => (
                                    <LineItem
                                        key={it.product_id}
                                        item={it}
                                        moq={moq}
                                        step={step}
                                        busy={busy}
                                        onChange={(q) => onChangeQty(it, q)}
                                        onRemove={() => onRemove(it)}
                                    />
                                ))}
                            </div>
                            <div className="py-3 flex items-center justify-end">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                        if (window.confirm(t('cart.clear_confirm'))) {
                                            setBusy(true);
                                            await clear();
                                            setBusy(false);
                                        }
                                    }}
                                    disabled={busy}
                                    data-testid="cart-clear-btn"
                                    className="text-gray-500 hover:text-red-600"
                                >
                                    <Trash2 size={14} className="mr-1.5" /> {t('cart.clear_cta')}
                                </Button>
                            </div>
                        </div>

                        {/* Summary panel */}
                        <aside className="space-y-4 lg:sticky lg:top-24 self-start" data-testid="cart-summary">
                            <div className="bg-white border border-[#EFE5E1] rounded-lg p-5 space-y-4">
                                <h3 className="text-sm uppercase tracking-[2px] text-gray-500 font-semibold">{t('cart.summary')}</h3>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">{t('cart.items')}</span>
                                <span className="text-sm font-medium" data-testid="summary-items">{cart?.item_count || 0}</span>
                            </div>
                            {!pricesHidden && (
                                <>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">{t('cart.subtotal_ht')}</span>
                                        <span className="text-sm font-medium" data-testid="summary-subtotal">{formatEur(cart?.subtotal)}</span>
                                    </div>
                                    <Separator />
                                    <div className="flex items-center justify-between">
                                        <span className="text-base font-semibold text-[#1A1A1A]">{t('cart.total')}</span>
                                        <span className="text-lg font-bold text-[#1A1A1A]" data-testid="summary-total">{formatEur(cart?.subtotal)}</span>
                                    </div>
                                    <p className="text-[11px] text-gray-400 leading-relaxed">{t('cart.vat_note')}</p>
                                </>
                            )}

                                <Textarea
                                    placeholder={t('cart.notes_placeholder')}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    maxLength={1000}
                                    data-testid="cart-notes"
                                    className="text-sm bg-[#FBF8F6] border-[#EFE5E1] focus:border-[#C08478] resize-none min-h-[80px]"
                                />

                                {cart?.has_moq_violations && (
                                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded" data-testid="summary-moq-warning">
                                        {t('cart.moq_warning', { moq })}
                                    </div>
                                )}
                                {!profileComplete && (
                                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded" data-testid="summary-profile-warning">
                                        {t('cart.profile_warning_prefix')} <button onClick={() => navigate('/portal/profile')} className="underline font-medium">{t('cart.profile_warning_link')}</button> {t('cart.profile_warning_suffix')}
                                    </div>
                                )}
                                {!approved && (
                                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded" data-testid="summary-approval-warning">
                                        {t('cart.approval_warning')}
                                    </div>
                                )}

                                <Button
                                    onClick={() => doSubmit('order')}
                                    disabled={!baseSubmittable || busy}
                                    data-testid="cart-submit-order-btn"
                                    className="w-full h-11 bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full font-medium disabled:opacity-40"
                                >
                                    {busy && busyKind === 'order' ? (
                                        <><Loader2 size={16} className="animate-spin mr-2" /> {t('cart.submitting')}</>
                                    ) : (
                                        <>{t('cart.submit_order')} <ArrowRight size={16} className="ml-2" /></>
                                    )}
                                </Button>

                                <Button
                                    onClick={() => doSubmit('quotation')}
                                    disabled={!baseSubmittable || busy}
                                    data-testid="cart-submit-quotation-btn"
                                    variant="outline"
                                    className="w-full h-11 rounded-full font-medium border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#FBF8F6] disabled:opacity-40"
                                >
                                    {busy && busyKind === 'quotation' ? (
                                        <><Loader2 size={16} className="animate-spin mr-2" /> {t('cart.submitting')}</>
                                    ) : (
                                        <><FileText size={15} className="mr-2" /> {t('cart.submit_quotation')}</>
                                    )}
                                </Button>

                                {pricesHidden && (
                                    <p className="text-[11px] text-gray-500 leading-relaxed flex items-start gap-1.5" data-testid="cart-prices-hidden-note">
                                        <EyeOff size={11} className="mt-0.5 shrink-0" />
                                        <span>{t('cart.prices_hidden_note')}</span>
                                    </p>
                                )}
                            </div>
                        </aside>
                    </div>
                )}
            </div>
        </PortalLayout>
    );
};

export default PortalCart;
